/* eslint-disable no-console */
import { io, Socket } from 'socket.io-client';
import store from '../store';
import { eventBus } from '@/utilities/event-bus';
import {
    CONSOLIDATED_REPORT_UPDATED_EVENT,
    SOCKET_CONNECTED_EVENT,
} from '@/constants/socketEvents';
import type {
    ConsolidatedReportFailedEvent,
    ConsolidatedReportReadyEvent,
} from '@/interfaces/apiInterfaces';

let socket: Socket | null = null;
let socketClient: SocketClient | null = null;

const DENIED_RETRY_BASE_MS = 5000;
const DENIED_RETRY_MAX_MS = 60000;

interface MaintenanceData {
    [key: string]: unknown;
    show: boolean;
}

type GreetingCallback = (response: string) => void;

export class SocketClient {
    readonly url: string;
    public client: Socket | null = null;
    private deniedRetryDelay = DENIED_RETRY_BASE_MS;
    private deniedRetryTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Initialize the socket client.
     *
     * @constructor
     */
    constructor() {
        this.url = process.env.VUE_APP_WS_URL ?? '';
    }

    /**
     * Read the current access token from cookies.
     *
     * @return {string} The access token, or an empty string if unavailable.
     */
    private getAccessToken(): string {
        // window refer to browser window as we dont know the interface of window
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (window as any).$cookies.get('accessToken') ?? '';
    }

    private scheduleDeniedRetry(): void {
        if (this.deniedRetryTimer || !this.getAccessToken()) return;

        this.deniedRetryTimer = setTimeout(() => {
            this.deniedRetryTimer = null;
            if (this.getAccessToken()) {
                this.client?.connect();
            }
        }, this.deniedRetryDelay);
        this.deniedRetryDelay = Math.min(this.deniedRetryDelay * 2, DENIED_RETRY_MAX_MS);
    }

    /**
     * Establish a connection to the socket server.
     *
     * @return {Promise.<Socket|null>} The socket client instance, or null if the connection failed.
     * @throws {Error} If the connection to the socket server failed.
     */
    async connect(): Promise<Socket | null> {
        return new Promise<Socket | null>((resolve) => {
            if (this.url && this.getAccessToken()) {
                // WebSocket only: the default `['polling', 'websocket']` starts with HTTP
                // long-polling, whose session id lives in the memory of a single backend
                // instance. With more than one ECS task behind the load balancer, a follow-up
                // poll routed to another task fails with "Session ID unknown". The Mongo
                // adapter shares broadcasts across instances, not engine.io sessions.
                this.client = io(this.url, {
                    // Callback form: tokens rotate hourly, so read the cookie fresh on every
                    // reconnect attempt instead of replaying the token from construction time.
                    auth: (cb) => cb({ accessToken: this.getAccessToken() }),
                    transports: ['websocket'],
                });

                this.client.on('connect', () => {
                    console.log('Socket client connected');
                    socket = this.client;
                    this.deniedRetryDelay = DENIED_RETRY_BASE_MS;
                    eventBus.emit(SOCKET_CONNECTED_EVENT);
                    resolve(this.client);
                });

                this.client.on('GREETING', (data: unknown, callback: GreetingCallback) => {
                    console.log('Socket server event', data);
                    callback('Pong');
                });

                this.client.on('MAINTENANCE', (data: Omit<MaintenanceData, 'show'>) => {
                    store.dispatch('setMaintenanceData', {
                        ...data,
                        show: true,
                    });
                });

                this.client.on(
                    'CONSOLIDATED_REPORT_READY',
                    async (data: ConsolidatedReportReadyEvent) => {
                        eventBus.emit(CONSOLIDATED_REPORT_UPDATED_EVENT, data);
                        const { showConsolidatedReportReady } = await import(
                            '@/utilities/consolidatedReportToasts'
                        );
                        showConsolidatedReportReady(data);
                    },
                );

                this.client.on(
                    'CONSOLIDATED_REPORT_FAILED',
                    async (data: ConsolidatedReportFailedEvent) => {
                        eventBus.emit(CONSOLIDATED_REPORT_UPDATED_EVENT, data);
                        const { showConsolidatedReportFailed } = await import(
                            '@/utilities/consolidatedReportToasts'
                        );
                        showConsolidatedReportFailed(data);
                    },
                );

                this.client.on('disconnect', (reason: string) => {
                    console.log('Socket client disconnected ', reason);
                });

                this.client.on('connect_error', (error: Error) => {
                    if (this.client?.active) {
                        // temporary failure, the socket will automatically try to reconnect
                        console.log('Socket client connection failed', error.message);
                    } else {
                        // the connection was denied by the server
                        // in that case, `socket.connect()` must be manually called in order to reconnect
                        console.log('Socket client connection denied', error.message);
                        this.scheduleDeniedRetry();
                    }
                    resolve(null);
                });
            } else {
                console.log('Missing connection params');
                resolve(null);
            }
        });
    }
}

/**
 * Returns the current socket client instance.
 * You can use it to access socket client from other files
 *
 * @return {Socket|null} The socket client instance, or null if not connected.
 */
export const getSocketClient = (): Socket | null => socket;

export const initSocketClient = async (): Promise<void> => {
    if (socket) return;

    if (socketClient?.client) {
        socketClient.client.connect();
        return;
    }

    socketClient = socketClient ?? new SocketClient();
    await socketClient.connect();
};

export const reconnectSocketClient = (): void => {
    const client = socketClient?.client;
    if (!client) {
        initSocketClient();
        return;
    }

    client.disconnect();
    client.connect();
};

export const disconnectSocketClient = (): void => {
    socketClient?.client?.disconnect();
    socket = null;
};
